import React from "react";
import { ArrowDownLeft } from "lucide-react";
import EditableText from "./EditableText";
import { useContent } from "../context/ContentContext";

export default function Stats() {
  const { content } = useContent();
  const stats = content?.stats;
  if (!stats) return null;

  const items = stats.items ?? [];
  if (items.length === 0) return null;

  return (
    <section
      id="stats"
      className="relative py-24 md:py-32 bg-[#F9F8F6] border-t border-[#E2E0D8]"
      data-testid="stats-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-20 items-start">
          <div className="md:col-span-7">
            <EditableText
              path="stats.heading"
              value={stats.heading}
              as="h2"
              multiline
              className="font-serif text-[#1A1D1A] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold text-balance"
              testId="stats-heading"
            />
          </div>
          <div className="md:col-span-4 md:col-start-9 md:pt-4">
            <EditableText
              path="stats.intro"
              value={stats.intro}
              as="p"
              multiline
              className="text-[#1A1D1A]/80 text-base md:text-lg leading-relaxed"
              testId="stats-intro"
            />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {items.map((item, i) => (
            <StatCard key={item.id} item={item} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ item, i }) {
  return (
    <article
      className="group relative overflow-hidden border border-[#E2E0D8] bg-[#1A1D1A] aspect-[4/5] rounded-2xl"
      data-testid={`stat-card-${i + 1}`}
    >
      <img
        src={item.image_url}
        alt={item.label}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.035]"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70"
      />

      <aside
        className="absolute top-0 right-0 bottom-0 w-10 md:w-12 bg-[#00025C] text-[#F9F8F6] flex flex-col items-center justify-between py-5"
        aria-hidden
      >
        <EditableText
          path={`stats.items.${i}.number`}
          value={item.number}
          as="span"
          className="font-sans text-sm font-semibold tracking-[0.2em]"
          testId={`stat-${i + 1}-number`}
        />
        <div
          className="flex-1 flex items-center justify-center"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          <EditableText
            path={`stats.items.${i}.label`}
            value={item.label}
            as="span"
            className="font-serif text-lg md:text-xl font-semibold tracking-wide whitespace-nowrap"
            testId={`stat-${i + 1}-label`}
          />
        </div>
        <ArrowDownLeft size={16} strokeWidth={1.5} />
      </aside>

      <div className="relative z-10 p-6 md:p-7 pr-14 md:pr-16 max-w-[85%]">
        <EditableText
          path={`stats.items.${i}.description`}
          value={item.description}
          as="p"
          multiline
          className="text-[#F9F8F6] text-xs md:text-sm leading-[1.55] drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
          testId={`stat-${i + 1}-description`}
        />
      </div>

      <div className="absolute left-0 right-10 md:right-12 bottom-0 p-6 md:p-8 pr-4 z-10 flex items-end gap-2">
        <EditableText
          path={`stats.items.${i}.value`}
          value={item.value}
          as="span"
          className="font-serif text-[#F9F8F6] text-4xl md:text-5xl lg:text-[56px] leading-[0.95] font-semibold tracking-tight drop-shadow-[0_3px_18px_rgba(0,0,0,0.55)]"
          testId={`stat-${i + 1}-value`}
        />
        <EditableText
          path={`stats.items.${i}.unit`}
          value={item.unit}
          as="span"
          className="font-serif text-[#F9F8F6]/90 text-base md:text-lg lg:text-xl font-medium tracking-wide mb-1 md:mb-2 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
          testId={`stat-${i + 1}-unit`}
        />
      </div>
    </article>
  );
}
